import { useEffect, useState } from "react";
import { api, convert, FX_TO_USD } from "../lib/api";

/**
 * Two-way pricing engine shared by ProjectDetail and BulkBuyPanel.
 *
 * - Fetches the live UCS index spot price on mount (USD).
 * - Keeps `tons` and `amount` (in `payCur`) in sync via the index, using
 *   `lastEdited` as the source of truth to avoid feedback loops.
 *
 * Returns { tons, amount, payCur, indexUsd, setTons, setAmount, setPayCur,
 *           setLastEdited, totalUsd }.
 */
export default function usePricing({ initialPayCur = "USD", initialTons = 1, initialIndexUsd = 35.80 } = {}) {
  const [tons, setTons] = useState(initialTons);
  const [amount, setAmount] = useState("");
  const [payCur, setPayCur] = useState(initialPayCur);
  const [lastEdited, setLastEdited] = useState("tons"); // "tons" | "amount"
  const [indexUsd, setIndexUsd] = useState(initialIndexUsd);

  useEffect(() => {
    api.get("/market/ucs-index").then((r) => {
      const usd = r.data?.spot?.USD;
      if (typeof usd === "number" && usd > 0) setIndexUsd(usd);
    }).catch(() => {});
  }, []);

  // tons → amount
  useEffect(() => {
    if (lastEdited !== "tons") return;
    const v = Number(tons || 0) * convert(indexUsd, payCur);
    setAmount(v > 0 ? v.toFixed(2) : "");
  }, [tons, payCur, indexUsd, lastEdited]);

  // amount → tons
  useEffect(() => {
    if (lastEdited !== "amount") return;
    const usd = Number(amount || 0) * (FX_TO_USD[payCur] || 1);
    const t = indexUsd > 0 ? usd / indexUsd : 0;
    setTons(t > 0 ? Number(t.toFixed(4)) : 0);
  }, [amount, payCur, indexUsd, lastEdited]);

  const totalUsd = Number(tons || 0) * indexUsd;

  return {
    tons, amount, payCur, indexUsd, lastEdited, totalUsd,
    setTons, setAmount, setPayCur, setLastEdited,
  };
}
